import {
  BankStatementRuleExpressionFormDto,
  type BankStatementRuleExpressionFormInterface,
} from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-expression-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';

import * as yup from 'yup';
import { pick, isNil } from 'lodash';

/** Bank statement rule create and update API's request body - DTO signature */
export interface BankStatementRuleFormInterface {
  /** Rule display name (for UI) */
  name: string;
  /** If `true`, documents matching this rule will be ignored */
  exclude: boolean;

  // Analytical features relations (part of rule)
  /** If defined, then documents matching this rule will be assigned this direction */
  direction?: DirectionFindInterface;
  /** If defined, then documents matching this rule will be assigned this unit */
  unit?: UnitFindInterface;
  /** If defined, then documents matching this rule will be assigned this filial */
  filial?: FilialFindInterface;
  /** If defined, then documents matching this rule will be assigned this deal */
  deal?: DealFindInterface;

  /** Rule expressions - conditions joined by operators */
  expressions: BankStatementRuleExpressionFormInterface[];
}

/**
 * Returns if the received form has at least one defined analytical feature. Needed for additional form validation.
 *
 * @param dto Form to check ({@link BankStatementRuleFormInterface} instance or object with same signature)
 *
 * @returns `true` if form contains least one defined analytical feature.
 */
export function hasAnalyticalFeature(dto: BankStatementRuleFormInterface): boolean {
  const partial = pick(dto, ['direction', 'unit', 'filial', 'deal']);
  return Object
    .values(partial)
    .some(item => !isNil(item?.id));
}

/** Bank statement rule create and update API's request body - DTO schema */
export const BankStatementRuleFormDto = SerdeDtoClass.create<BankStatementRuleFormInterface>(DocumentDtoKeysEnum.BANK_STATEMENT_RULE_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  exclude: {
    label: 'Исключающее правило',
    serializer: yup.boolean(),
  },
  direction: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  deal: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
  },
  expressions: {
    label: 'Выражения',
    default: [],
    nested: BankStatementRuleExpressionFormDto,
    serializer: (_, nested) => yup
      .array(nested)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
  },
});
